// Named resume versions held in browser memory.
//
// A version is a deep snapshot, not a reference to the live draft, so editing the
// draft after saving cannot change a saved version. Restoring replaces the working
// draft with a copy of a snapshot; the versions list itself is left alone.

import { nextId } from '../profile/model';
import type {
  AnyResumeEntry,
  ResumeComparison,
  ResumeDiffLine,
  ResumeDraft,
  ResumeSectionId,
  ResumeVersion,
} from './types';
import { RESUME_SECTION_LABEL } from './types';

/** Structured deep copy that cannot share nested arrays or objects with its source. */
export function cloneDraft(draft: ResumeDraft): ResumeDraft {
  return structuredClone(draft);
}

export function saveVersion(
  draft: ResumeDraft,
  name: string,
  savedAt: string = new Date().toISOString(),
): ResumeVersion {
  return {
    id: nextId('version'),
    name,
    savedAt,
    template: draft.template,
    snapshot: cloneDraft(draft),
  };
}

/** Restores a working draft from a saved snapshot without mutating the snapshot. */
export function restoreVersion(version: ResumeVersion): ResumeDraft {
  return cloneDraft(version.snapshot);
}

function entryDiffLabel(entry: AnyResumeEntry): string {
  if ('organization' in entry) return `${entry.role} — ${entry.organization}`;
  if ('institution' in entry) return `${entry.credential} — ${entry.institution}`;
  return entry.name;
}

function entryText(entry: AnyResumeEntry): string {
  const clone: Record<string, unknown> = { ...entry };
  delete clone.id;
  delete clone.state;
  delete clone.evidence;
  delete clone.edited;
  return JSON.stringify(clone);
}

function diffSectionMeta(before: ResumeDraft, after: ResumeDraft): ResumeDiffLine[] {
  const lines: ResumeDiffLine[] = [];
  if (before.name !== after.name) {
    lines.push({ kind: 'changed', section: 'meta', label: 'Name', before: before.name, after: after.name });
  }
  if (before.targetRole !== after.targetRole) {
    lines.push({
      kind: 'changed',
      section: 'meta',
      label: 'Target role',
      before: before.targetRole,
      after: after.targetRole,
    });
  }
  if (before.template !== after.template) {
    lines.push({
      kind: 'changed',
      section: 'meta',
      label: 'Template',
      before: before.template,
      after: after.template,
    });
  }
  const beforeOrder = before.sectionOrder.join(', ');
  const afterOrder = after.sectionOrder.join(', ');
  if (beforeOrder !== afterOrder) {
    lines.push({
      kind: 'changed',
      section: 'meta',
      label: 'Section order',
      before: beforeOrder,
      after: afterOrder,
    });
  }
  return lines;
}

function diffSection(
  section: ResumeSectionId,
  before: ResumeDraft,
  after: ResumeDraft,
): ResumeDiffLine[] {
  const lines: ResumeDiffLine[] = [];
  const label = RESUME_SECTION_LABEL[section];

  if (section === 'summary') {
    if (before.summary.value !== after.summary.value) {
      lines.push({
        kind: 'changed',
        section,
        label,
        before: before.summary.value,
        after: after.summary.value,
      });
    }
    return lines;
  }

  const beforeList: AnyResumeEntry[] = before[section] as AnyResumeEntry[];
  const afterList: AnyResumeEntry[] = after[section] as AnyResumeEntry[];
  const beforeById = new Map(beforeList.map((entry) => [entry.id, entry]));
  const afterById = new Map(afterList.map((entry) => [entry.id, entry]));

  for (const entry of beforeList) {
    const afterEntry = afterById.get(entry.id);
    if (!afterEntry) {
      lines.push({ kind: 'removed', section, label: entryDiffLabel(entry), before: entryDiffLabel(entry), after: '' });
    } else if (entryText(entry) !== entryText(afterEntry)) {
      lines.push({
        kind: 'changed',
        section,
        label: entryDiffLabel(afterEntry),
        before: entryDiffLabel(entry),
        after: entryDiffLabel(afterEntry),
      });
    }
  }
  for (const entry of afterList) {
    if (!beforeById.has(entry.id)) {
      lines.push({ kind: 'added', section, label: entryDiffLabel(entry), before: '', after: entryDiffLabel(entry) });
    }
  }
  return lines;
}

/** Ordered, human-readable difference between two snapshots. */
export function compareVersions(
  from: ResumeVersion,
  to: ResumeVersion,
): ResumeComparison {
  const lines: ResumeDiffLine[] = [
    ...diffSectionMeta(from.snapshot, to.snapshot),
  ];
  const sections = new Set<ResumeSectionId>([
    ...from.snapshot.sectionOrder,
    ...to.snapshot.sectionOrder,
  ]);
  for (const section of sections) {
    lines.push(...diffSection(section, from.snapshot, to.snapshot));
  }
  const identical = lines.length === 0;
  return { fromVersionId: from.id, toVersionId: to.id, lines, identical };
}
